import { cn } from "@/lib/utils";

export type ProgressTone = "success" | "warning" | "danger" | "info" | "pending";

/** Accessible reason for a bar whose value is not a usable number (ALL-5643). */
export const PROGRESS_UNAVAILABLE_LABEL = "Progress unavailable — no measurement recorded";

const TONES: Record<ProgressTone, string> = {
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-danger",
  info: "bg-info",
  pending: "bg-pending",
};

export function Progress({
  value,
  tone = "info",
  size = "md",
  label,
  showLabel = true,
  className,
}: {
  value: number;
  tone?: ProgressTone;
  size?: "sm" | "md";
  label?: string;
  showLabel?: boolean;
  className?: string;
}) {
  // A value that is not a usable number (NaN, from a 0-of-0 ratio) is an
  // unknown, not a measurement: it must never render as "NaN%". A real 0 is a
  // measurement and stays 0%. A blank label is no label, so the bar keeps its
  // default accessible name rather than an empty one.
  const known = Number.isFinite(value);
  const pct = known ? Math.max(0, Math.min(100, value)) : 0;
  return (
    <div className={cn("w-full", className)}>
      {showLabel && (
        <div className="mb-1 flex items-center justify-between gap-2 text-xs">
          {label && <span className="font-semibold text-ink">{label}</span>}
          <span className="text-muted">{known ? `${pct}%` : "—"}</span>
        </div>
      )}
      <div
        role="progressbar"
        aria-valuenow={known ? pct : undefined}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuetext={known ? undefined : PROGRESS_UNAVAILABLE_LABEL}
        aria-label={label?.trim() ? label : "Progress"}
        className={cn("w-full overflow-hidden rounded-full bg-disabled-soft", size === "sm" ? "h-1.5" : "h-2.5")}
      >
        <div
          className={cn("h-full rounded-full transition-[width] duration-300", TONES[tone])}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}